import request from '@/utils/request'
import {
  getToken,
  setToken,
  clearToken,
  readCode,
  stripCode,
  redirectToOauth,
  resetRedirectGuard
} from '@/utils/wxsession'

/**
 * 企业微信授权编排
 *
 * 用法：所有需要鉴权的数据请求前先 await / then 一次 ensureAuth()，
 * 拿到 { ok: true } 再发业务请求。token 由 request.js 的拦截器统一塞进请求头，
 * 业务代码不需要再手工传 code。
 *
 * ensureAuth 内部做了单飞：一个页面同时有多个请求时，code 只会被换一次。
 */

let pending = null
let profile = null

export function getProfile () {
  return profile
}

export function ensureAuth () {
  if (pending) {
    return pending
  }
  pending = exchange().then(res => {
    pending = null
    return res
  }).catch(err => {
    pending = null
    return { ok: false, message: (err && err.message) || '授权异常' }
  })
  return pending
}

function exchange () {
  const code = readCode()

  if (!code) {
    if (getToken()) {
      return Promise.resolve({ ok: true, profile: profile })
    }
    return Promise.resolve(reauth('登录状态已失效'))
  }

  // 先抹掉地址栏里的 code，避免用户刷新时重放同一个一次性凭据
  stripCode()

  return request({
    method: 'POST',
    url: '/api/wxLogin',
    data: { code: code }
  }).then(res => {
    const body = res.data || {}
    if (body.status === 'success' && body.token) {
      setToken(body.token)
      resetRedirectGuard()
      profile = {
        userid: body.userid,
        username: body.username,
        isuser: body.isuser
      }
      return { ok: true, profile: profile }
    }

    // code 换票失败（已被消费 / 已过期 / 无权限）
    // 本地还有可用的 token 就继续用，否则重新走授权
    if (getToken()) {
      return { ok: true, profile: profile }
    }
    if (body.reauth === false) {
      // 明确是权限问题，重新授权也没用，直接把原因抛给页面
      return { ok: false, message: body.message || '您没有权限查看' }
    }
    return reauth(body.message)
  }).catch(() => {
    if (getToken()) {
      return { ok: true, profile: profile }
    }
    return { ok: false, message: '网络异常，请稍后重试' }
  })
}

function reauth (message) {
  clearToken()
  profile = null
  const redirected = redirectToOauth()
  return {
    ok: false,
    redirected: redirected,
    message: redirected
      ? '正在重新授权…'
      : (message || '授权失败，请退出后从企业微信工作台重新进入')
  }
}

/**
 * 主动作废本地票据（调试或退出时用）
 */
export function logout () {
  clearToken()
  profile = null
  pending = null
}
